import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Crosshair,
  MapPin,
  Phone,
  Navigation,
  Loader2,
  CheckCircle2,
  Clock,
  Search,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Emergency Service Finder
 *
 * NOT a 911 / dispatch service — purely a lookup tool that helps users
 * locate nearby RanchWorks providers offering emergency animal/ranch help.
 *
 * Flow:
 *   1. Confirm/enter location
 *   2. Choose emergency type
 *   3. View matching providers (sorted nearest first when coords available)
 */
export default function EmergencyFinderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [location, setLocation] = useState('');
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [results, setResults] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/emergency/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location services unavailable on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocation(
          `Current location (${pos.coords.latitude.toFixed(3)}, ${pos.coords.longitude.toFixed(3)})`
        );
        setLocating(false);
        toast.success('Location captured');
      },
      (err) => {
        setLocating(false);
        toast.error(err.message || 'Could not get your location');
      },
      { timeout: 8000, maximumAge: 60_000 }
    );
  };

  const goToCategoryStep = async () => {
    if (!location.trim()) {
      toast.error('Enter a location or use your current location');
      return;
    }
    // If user typed a location but we don't have coords yet, try to geocode
    // it via Nominatim so the result list can use real distance filtering.
    if (!coords) {
      setLocating(true);
      try {
        const res = await axios.get(`${API_URL}/api/geocode`, {
          params: { q: location.trim() },
        });
        if (res.data?.found) {
          setCoords({ lat: res.data.latitude, lng: res.data.longitude });
        }
      } catch {
        // silent — fall through to text-match on the backend
      } finally {
        setLocating(false);
      }
    }
    setStep(2);
  };

  const runSearch = async (categoryValue) => {
    setSelectedCategory(categoryValue);
    setStep(3);
    setSearching(true);
    try {
      const params = { category: categoryValue };
      if (coords) {
        params.latitude = coords.lat;
        params.longitude = coords.lng;
      } else if (location) {
        params.location = location;
      }
      const res = await axios.get(`${API_URL}/api/emergency/providers`, { params });
      setResults(res.data?.providers || []);
      setDisclaimer(res.data?.disclaimer || '');
    } catch (e) {
      toast.error('Could not load providers');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="emergency-finder-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-card border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-3 py-3 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (step === 1 ? navigate('/') : setStep(step - 1))}
            data-testid="emergency-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Logo variant="compact" size="sm" />
          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-4 space-y-4 pb-12">
        {/* Title + tagline */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="emergency-title">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            Emergency Service Finder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Find ranch &amp; animal-care providers offering emergency help near you.
          </p>
        </div>

        {/* Disclaimer */}
        <Card className="border-amber-300 bg-amber-50" data-testid="emergency-disclaimer">
          <CardContent className="p-3 text-sm text-amber-900 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              RanchWorks helps locate service providers but is{' '}
              <strong>not an emergency response agency</strong>. For life-threatening situations
              call your local emergency number.
            </p>
          </CardContent>
        </Card>

        {/* Stepper */}
        <Stepper step={step} />

        {/* Step 1 — Location */}
        {step === 1 && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium">Where do you need help?</p>
              <div className="flex gap-2">
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Town, ZIP, or property name"
                  data-testid="emergency-location-input"
                />
                <Button
                  variant="outline"
                  onClick={useMyLocation}
                  disabled={locating}
                  data-testid="emergency-use-location-btn"
                >
                  {locating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Crosshair className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline ml-1">Use my location</span>
                </Button>
              </div>
              {coords && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  Coordinates captured — results will be sorted by distance.
                </p>
              )}
              <Button
                className="w-full"
                onClick={goToCategoryStep}
                data-testid="emergency-next-btn"
              >
                Next: Choose Emergency Type <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2 — Category */}
        {step === 2 && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium">What kind of help do you need?</p>
              <div className="grid grid-cols-2 gap-2" data-testid="emergency-category-grid">
                {categories.map((c) => (
                  <Button
                    key={c.value}
                    variant="outline"
                    className="h-14 justify-start text-left"
                    onClick={() => runSearch(c.value)}
                    data-testid={`emergency-cat-${c.value}`}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                    {c.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3 — Results */}
        {step === 3 && (
          <div className="space-y-3" data-testid="emergency-results">
            <Card>
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Showing</span>
                  <Badge variant="secondary">
                    {categories.find((c) => c.value === selectedCategory)?.label || selectedCategory}
                  </Badge>
                  <span className="text-muted-foreground">near</span>
                  <span className="font-medium truncate max-w-[180px]">{location}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setStep(2)}
                  data-testid="emergency-change-category"
                >
                  Change
                </Button>
              </CardContent>
            </Card>

            {searching ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : results.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center space-y-2">
                  <p className="font-medium">No providers found nearby</p>
                  <p className="text-sm text-muted-foreground">
                    Try a different category or broaden your location.
                  </p>
                  <Link to="/" className="text-primary text-sm underline inline-block mt-1">
                    Browse all providers
                  </Link>
                </CardContent>
              </Card>
            ) : (
              results.map((p) => <ProviderEmergencyCard key={p.id} p={p} />)
            )}

            {disclaimer && (
              <p className="text-[11px] text-muted-foreground text-center pt-2">{disclaimer}</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stepper({ step }) {
  const steps = [
    { n: 1, label: 'Location' },
    { n: 2, label: 'Type' },
    { n: 3, label: 'Providers' },
  ];
  return (
    <div className="flex items-center justify-between bg-muted/40 rounded-lg p-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-1.5">
            <div
              className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= s.n
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted-foreground/20 text-muted-foreground'
              }`}
            >
              {s.n}
            </div>
            <span
              className={`text-xs ${step >= s.n ? 'font-medium' : 'text-muted-foreground'}`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className="flex-1 h-px bg-muted-foreground/20 mx-2" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ProviderEmergencyCard({ p }) {
  const phone = p.phone;
  const tel = phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null;
  const directions = p.latitude && p.longitude
    ? `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`
    : p.city
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.city}, ${p.state || ''}`)}`
    : null;

  return (
    <Card data-testid={`emergency-provider-${p.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-lg leading-tight truncate">{p.businessName}</p>
            <p className="text-xs text-muted-foreground">{p.ownerName}</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {p.currentlyAvailable && (
              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Available now
              </Badge>
            )}
            {p.available24hr && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" /> 24 hr
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1">
          {(p.emergencyCategories || []).slice(0, 3).map((c) => (
            <Badge key={c} variant="secondary" className="text-[10px]">
              {c.replace(/_/g, ' ').toLowerCase()}
            </Badge>
          ))}
        </div>

        <div className="text-sm text-muted-foreground space-y-0.5">
          <p className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            {p.city}
            {p.state ? `, ${p.state}` : ''}
            {p.distanceMiles != null && (
              <span className="ml-1 text-foreground font-medium">
                · {p.distanceMiles} mi
              </span>
            )}
          </p>
          {p.emergencyNotes && (
            <p className="text-xs italic line-clamp-2">"{p.emergencyNotes}"</p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2 border-t">
          <Button
            size="sm"
            className="h-10"
            asChild={!!tel}
            disabled={!tel}
            data-testid={`call-${p.id}`}
          >
            {tel ? (
              <a href={tel}>
                <Phone className="h-4 w-4 mr-1" /> Call
              </a>
            ) : (
              <span>
                <Phone className="h-4 w-4 mr-1" /> Call
              </span>
            )}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="h-10"
            asChild
            data-testid={`request-${p.id}`}
          >
            <Link to={`/providers/${p.slug}`}>Request</Link>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-10"
            asChild={!!directions}
            disabled={!directions}
            data-testid={`directions-${p.id}`}
          >
            {directions ? (
              <a href={directions} target="_blank" rel="noopener noreferrer">
                <Navigation className="h-4 w-4 mr-1" /> Map
              </a>
            ) : (
              <span>
                <Navigation className="h-4 w-4 mr-1" /> Map
              </span>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
